import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import {
  CardapioEstabelecimentoDTO,
  CardapioCategoriaDTO,
} from '../../models/cardapio-digital/cardapio-digital.model';

/**
 * Servico responsavel por gerenciar SEO do cardapio digital:
 * - Schema.org JSON-LD (tipo Restaurant + Menu)
 * - Open Graph meta tags (compartilhamento WhatsApp/Facebook)
 * - Twitter Card meta tags
 * - Title dinamico
 * - Theme color dinamico
 * - PWA manifest dinamico
 * - Favicon dinamico
 *
 * Deve ser chamado apos carregar os dados do estabelecimento na rota publica.
 * Limpar tudo no ngOnDestroy do componente via destroy().
 */
@Injectable({ providedIn: 'root' })
export class CardapioSeoService {

  private tituloOriginal = '';
  private themeColorOriginal = '';
  private faviconOriginal = '';
  private manifestBlobUrl: string | null = null;

  private readonly SCHEMA_SCRIPT_ID = 'cardapio-schema-org';

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private meta: Meta,
    private titleService: Title,
    private translate: TranslateService,
  ) {}

  /**
   * Aplica todas as otimizacoes de SEO para o cardapio digital.
   * Chamar apos processarResposta() no componente principal.
   */
  aplicar(
    estabelecimento: CardapioEstabelecimentoDTO,
    categorias: CardapioCategoriaDTO[],
    moedaSimbolo: string,
    slug: string | null,
  ): void {
    if (!estabelecimento || !slug) return;

    this.salvarEstadoOriginal();
    this.injetarSchemaOrg(estabelecimento, categorias, moedaSimbolo);
    this.atualizarMetaTags(estabelecimento, slug);
    this.atualizarTitle(estabelecimento);
    this.atualizarThemeColor(estabelecimento);
    this.atualizarFavicon(estabelecimento);
    this.atualizarManifestPWA(estabelecimento, slug);
  }

  /**
   * Remove todos os elementos SEO injetados e restaura o estado original.
   * Chamar no ngOnDestroy do componente.
   */
  destroy(): void {
    this.removerSchemaOrg();
    this.removerMetaTags();
    this.restaurarTitle();
    this.restaurarThemeColor();
    this.restaurarFavicon();
    this.removerManifestPWA();
  }

  // =====================================================
  // Schema.org JSON-LD
  // =====================================================

  private injetarSchemaOrg(
    estabelecimento: CardapioEstabelecimentoDTO,
    categorias: CardapioCategoriaDTO[],
    moedaSimbolo: string,
  ): void {
    // Remover anterior se existir
    this.removerSchemaOrg();

    const schema: Record<string, any> = {
      '@context': 'https://schema.org',
      '@type': 'Restaurant',
      'name': estabelecimento.nome,
      'url': this.document.location.href,
    };

    if (estabelecimento.banner || estabelecimento.logo) {
      schema['image'] = estabelecimento.banner || estabelecimento.logo;
    }

    if (estabelecimento.endereco) {
      schema['address'] = {
        '@type': 'PostalAddress',
        'streetAddress': estabelecimento.endereco,
      };
    }

    if (estabelecimento.telefoneWhatsapp) {
      schema['telephone'] = estabelecimento.telefoneWhatsapp;
    }

    if (estabelecimento.horarioFuncionamento) {
      schema['openingHours'] = estabelecimento.horarioFuncionamento;
    }

    // Menu com categorias e produtos
    if (categorias && categorias.length > 0) {
      schema['hasMenu'] = {
        '@type': 'Menu',
        'hasMenuSection': categorias.map(cat => ({
          '@type': 'MenuSection',
          'name': cat.nome,
          'hasMenuItem': cat.produtos.map(prod => {
            const item: Record<string, any> = {
              '@type': 'MenuItem',
              'name': prod.nome,
              'offers': {
                '@type': 'Offer',
                'price': (prod.precoPromocional || prod.preco).toString(),
                'priceCurrency': moedaSimbolo,
              },
            };
            if (prod.descricao) {
              item['description'] = prod.descricao;
            }
            if (prod.foto) {
              item['image'] = prod.foto;
            }
            return item;
          }),
        })),
      };
    }

    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.id = this.SCHEMA_SCRIPT_ID;
    script.text = JSON.stringify(schema);
    this.document.head.appendChild(script);
  }

  private removerSchemaOrg(): void {
    const existing = this.document.getElementById(this.SCHEMA_SCRIPT_ID);
    if (existing) {
      existing.remove();
    }
  }

  // =====================================================
  // Open Graph + Twitter Card meta tags
  // =====================================================

  private atualizarMetaTags(
    estabelecimento: CardapioEstabelecimentoDTO,
    slug: string,
  ): void {
    const titulo = `${estabelecimento.nome} - ${this.translate.instant('cardapio_digital.mini_shop')}`;
    const descricao = `Faca seu pedido online em ${estabelecimento.nome}`;
    const imagem = estabelecimento.banner || estabelecimento.logo || '';
    const url = this.document.location.href;

    // Open Graph
    this.setMetaTag('og:title', titulo, 'property');
    this.setMetaTag('og:description', descricao, 'property');
    this.setMetaTag('og:url', url, 'property');
    this.setMetaTag('og:type', 'website', 'property');
    if (imagem) {
      this.setMetaTag('og:image', imagem, 'property');
    }

    // Twitter Card
    this.setMetaTag('twitter:card', 'summary_large_image', 'name');
    this.setMetaTag('twitter:title', titulo, 'name');
    this.setMetaTag('twitter:description', descricao, 'name');
    if (imagem) {
      this.setMetaTag('twitter:image', imagem, 'name');
    }

    // Meta description padrao
    this.setMetaTag('description', descricao, 'name');
  }

  private setMetaTag(nameOrProperty: string, content: string, attrType: 'name' | 'property'): void {
    const selector = `${attrType}="${nameOrProperty}"`;
    const existingTag = this.meta.getTag(selector);

    if (existingTag) {
      this.meta.updateTag({ [attrType]: nameOrProperty, content });
    } else {
      this.meta.addTag({ [attrType]: nameOrProperty, content });
    }
  }

  private removerMetaTags(): void {
    const tags = [
      'og:title', 'og:description', 'og:url', 'og:type', 'og:image',
      'twitter:card', 'twitter:title', 'twitter:description', 'twitter:image',
    ];

    tags.forEach(tag => {
      const isOg = tag.startsWith('og:');
      const selector = isOg ? `property="${tag}"` : `name="${tag}"`;
      this.meta.removeTag(selector);
    });

    // Remover description adicionada
    this.meta.removeTag('name="description"');
  }

  // =====================================================
  // Title dinamico
  // =====================================================

  private atualizarTitle(estabelecimento: CardapioEstabelecimentoDTO): void {
    const titulo = `${estabelecimento.nome} - ${this.translate.instant('cardapio_digital.mini_shop')}`;
    this.titleService.setTitle(titulo);
  }

  private restaurarTitle(): void {
    if (this.tituloOriginal) {
      this.titleService.setTitle(this.tituloOriginal);
    }
  }

  // =====================================================
  // Theme Color
  // =====================================================

  private atualizarThemeColor(estabelecimento: CardapioEstabelecimentoDTO): void {
    const cor = estabelecimento.botoesCorDeFundo?.trim() || '#007BFF';
    let metaTag = this.document.querySelector('meta[name="theme-color"]') as HTMLMetaElement;

    if (!metaTag) {
      metaTag = this.document.createElement('meta');
      metaTag.name = 'theme-color';
      this.document.head.appendChild(metaTag);
    }

    metaTag.content = cor;
  }

  private restaurarThemeColor(): void {
    const metaTag = this.document.querySelector('meta[name="theme-color"]') as HTMLMetaElement;
    if (metaTag) {
      if (this.themeColorOriginal) {
        metaTag.content = this.themeColorOriginal;
      } else {
        metaTag.remove();
      }
    }
  }

  // =====================================================
  // Favicon dinamico
  // =====================================================

  private atualizarFavicon(estabelecimento: CardapioEstabelecimentoDTO): void {
    if (!estabelecimento.logo) return;

    let link = this.document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'icon';
      this.document.head.appendChild(link);
    }
    link.href = estabelecimento.logo;
  }

  private restaurarFavicon(): void {
    const link = this.document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    if (link && this.faviconOriginal) {
      link.href = this.faviconOriginal;
    }
  }

  // =====================================================
  // PWA Manifest dinamico
  // =====================================================

  private atualizarManifestPWA(
    estabelecimento: CardapioEstabelecimentoDTO,
    slug: string,
  ): void {
    const manifest = {
      name: estabelecimento.nome,
      short_name: estabelecimento.nome,
      start_url: `/menu/${slug}`,
      display: 'standalone',
      background_color: estabelecimento.corDeFundo?.trim() || '#ffffff',
      theme_color: estabelecimento.botoesCorDeFundo?.trim() || '#007BFF',
      icons: estabelecimento.logo ? [
        { src: estabelecimento.logo, sizes: '192x192', type: 'image/png' },
        { src: estabelecimento.logo, sizes: '512x512', type: 'image/png' },
      ] : [],
    };

    const blob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
    this.manifestBlobUrl = URL.createObjectURL(blob);

    let link = this.document.querySelector('link[rel="manifest"]') as HTMLLinkElement;
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'manifest';
      this.document.head.appendChild(link);
    }
    link.href = this.manifestBlobUrl;
  }

  private removerManifestPWA(): void {
    if (this.manifestBlobUrl) {
      URL.revokeObjectURL(this.manifestBlobUrl);
      this.manifestBlobUrl = null;
    }

    const link = this.document.querySelector('link[rel="manifest"]') as HTMLLinkElement;
    if (link) {
      link.remove();
    }
  }

  // =====================================================
  // Estado original
  // =====================================================

  private salvarEstadoOriginal(): void {
    this.tituloOriginal = this.titleService.getTitle();

    const themeColorMeta = this.document.querySelector('meta[name="theme-color"]') as HTMLMetaElement;
    this.themeColorOriginal = themeColorMeta?.content || '';

    const faviconLink = this.document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    this.faviconOriginal = faviconLink?.href || '';
  }
}
